import React from "react";
import { reportError } from "@/utils/traceup/traceup";
import {
  isStaleChunkError,
  reloadOnceForStaleChunk,
} from "@/utils/staleChunkReload";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Spec 027 — Barrera de error global de render.
 *
 * React solo captura errores de render vía error boundary de clase. Envuelve
 * <App/> en main.tsx. Ante un crash muestra una pantalla amigable (nunca una
 * página en blanco ni diálogos nativos) y reporta el error a TraceUp.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // Chunk obsoleto tras deploy: no es un bug de la app. Recargar una vez trae
    // los assets vigentes; si lo logra, no se reporta (sería ruido esperado).
    if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return;

    reportError({
      error,
      source: "frontend-render",
      extraMetadata: { componentStack: info?.componentStack },
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          background: "#f7f7fb",
          color: "#1f2937",
        }}
      >
        <div style={{ fontSize: "48px" }}>😕</div>
        <h1 style={{ fontSize: "20px", fontWeight: 600, margin: 0 }}>
          Ocurrió un error inesperado
        </h1>
        <p style={{ maxWidth: "420px", color: "#6b7280", margin: 0 }}>
          Lo sentimos, algo salió mal en esta pantalla. Puedes recargar para
          volver a intentarlo. Si el problema continúa, contacta a soporte.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            marginTop: "8px",
            padding: "10px 24px",
            borderRadius: "9999px",
            border: "none",
            background: "#937cf4",
            color: "#fff",
            fontWeight: 600,
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          Recargar
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
